import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useMemo } from 'react';
import {
  useGetBusinessUnitAllowedBrandsQuery,
  useGetBusinessUnitBranchOfficesQuery,
  useGetBusinessUnitSourcesQuery,
} from 'services/api/businessUnits.api';
import { useGetUniqueValuesOfLeadQuery } from 'services/api/leads.api';
import { getTopicColor } from 'utils/topicColor';

import type { FilterOption } from './types';

/**
 * Fetches dynamic filter options (sources, brands, campaigns, topics, branchOffices) for lead-related views.
 * Returns them in FilterOption[] format ready for MasterFilter's dynamicOptions prop.
 *
 * `skipModalOnly` lets callers defer fetching the option groups only consumed by the
 * advanced filter modal (origins, branchOffices, campaigns) until the modal is open.
 * Set it to true on initial render to avoid eager requests; flip it false when the
 * user opens the modal — or earlier if those options are needed for chip labels or
 * table column dropdowns.
 */
export function useLeadFilterOptions(
  businessUnitId: number | null,
  startDate: string | null,
  endDate: string | null,
  type: 'lead' | 'client' = 'lead',
  options?: {
    skipOrigins?: boolean;
    skipBranchOffices?: boolean;
    skipCampaigns?: boolean;
    skipBrands?: boolean;
  }
) {
  const { getBusinessUnitById } = useBusinessUnits();
  const skip = !businessUnitId || businessUnitId === -1;
  const { skipOrigins = false, skipBranchOffices = false, skipCampaigns = false, skipBrands = false } = options ?? {};

  // Sources: use BU sources for clients, unique lead values for leads
  const { data: buSources } = useGetBusinessUnitSourcesQuery(
    { businessUnitId: businessUnitId || 0 },
    { skip: skip || type !== 'client' }
  );
  const { data: leadSources } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'source' },
    { skip: skip || type !== 'lead' }
  );

  // Origins (categorized LEAD_ORIGINS enum) — only those present in fetched leads.
  const { data: leadOrigins } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'origin' },
    { skip: skip || type !== 'lead' || skipOrigins }
  );

  // SubOrigins (subcategory of origin) — only those present in fetched leads.
  const { data: leadSubOrigins } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'subOrigin' },
    { skip: skip || type !== 'lead' || skipOrigins }
  );

  // Brands: prefer static config from BU; fall back to DB unique values if config is empty
  const { data: allowedBrands, isSuccess: allowedBrandsLoaded } = useGetBusinessUnitAllowedBrandsQuery(
    { businessUnitId: businessUnitId || 0 },
    { skip: skip || skipBrands }
  );
  const hasStaticBrands = allowedBrandsLoaded && (allowedBrands?.length ?? 0) > 0;
  const { data: dbBrandsData } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'initialBrandOfInterest' },
    { skip: skip || !allowedBrandsLoaded || hasStaticBrands || skipBrands }
  );
  const { data: dbCurrentBrandsData } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'currentBrand' },
    { skip: skip || !allowedBrandsLoaded || hasStaticBrands }
  );

  // Branch offices: only from static config — no DB fallback
  const { data: staticBranchOffices } = useGetBusinessUnitBranchOfficesQuery(
    { businessUnitId: businessUnitId || 0 },
    { skip: skip || skipBranchOffices }
  );

  // Campaigns (outbound + whatsApp)
  const { data: outboundCampaigns } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'outboundCampaignId' },
    { skip: skip || skipCampaigns }
  );
  const { data: whatsAppCampaigns } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'whatsAppCampaignId' },
    { skip: skip || skipCampaigns }
  );

  // Topics: unique values across leads (covers BUs without conversationTaggingConfig)
  const { data: leadTopics } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: businessUnitId || 0, startDate, endDate, attribute: 'topics' },
    { skip }
  );

  const sourcesOptions: FilterOption[] = useMemo(() => {
    const raw = type === 'client' ? buSources : leadSources;
    return (raw ?? []).filter(Boolean).map((s) => ({ value: s, label: s }));
  }, [buSources, leadSources, type]);

  const originsOptions: FilterOption[] = useMemo(
    () => (leadOrigins ?? []).filter(Boolean).map((o) => ({ value: o, label: o })),
    [leadOrigins]
  );

  const subOriginsOptions: FilterOption[] = useMemo(
    () => (leadSubOrigins ?? []).filter(Boolean).map((o) => ({ value: o, label: o })),
    [leadSubOrigins]
  );

  const brandsOptions: FilterOption[] = useMemo(() => {
    const raw = hasStaticBrands ? allowedBrands : dbBrandsData;
    return (raw ?? []).filter(Boolean).map((b) => ({ value: b, label: b }));
  }, [hasStaticBrands, allowedBrands, dbBrandsData]);

  const currentBrandsOptions: FilterOption[] = useMemo(() => {
    if (hasStaticBrands) {
      return (allowedBrands ?? []).filter(Boolean).map((b) => ({ value: b, label: b }));
    }
    const opts = (dbCurrentBrandsData ?? [])
      .filter(Boolean)
      .filter((b) => b.toUpperCase() !== 'USADOS')
      .map((b) => ({ value: b, label: b }));
    opts.push({ value: 'USADOS', label: 'USADOS' });
    return opts;
  }, [hasStaticBrands, allowedBrands, dbCurrentBrandsData]);

  const branchOfficesOptions: FilterOption[] = useMemo(
    () => (staticBranchOffices ?? []).filter(Boolean).map((o) => ({ value: o, label: o })),
    [staticBranchOffices]
  );

  const campaignsOptions: FilterOption[] = useMemo(() => {
    const all = [...(outboundCampaigns ?? []), ...(whatsAppCampaigns ?? [])].filter(Boolean).sort();
    return [...new Set(all)].map((c) => ({ value: c, label: c }));
  }, [outboundCampaigns, whatsAppCampaigns]);

  const topicsOptions: FilterOption[] = useMemo(() => {
    const taggingConfig = businessUnitId ? getBusinessUnitById(businessUnitId)?.conversationTaggingConfig : null;
    const configMap = new Map<string, { color?: string | null; description?: string | null }>();
    (taggingConfig?.topics ?? []).forEach((t) => configMap.set(t.name, { color: t.color, description: t.description }));

    const names = new Set<string>(configMap.keys());
    (leadTopics ?? []).forEach((name) => {
      if (name) names.add(name);
    });

    return Array.from(names)
      .map((name) => {
        const cfg = configMap.get(name);
        return {
          value: name,
          label: name,
          color: getTopicColor(name, cfg?.color),
          description: cfg?.description ?? undefined,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [businessUnitId, getBusinessUnitById, leadTopics]);

  return useMemo(
    () => ({
      sources: sourcesOptions,
      origins: originsOptions,
      subOrigins: subOriginsOptions,
      brands: brandsOptions,
      currentBrands: currentBrandsOptions,
      campaigns: campaignsOptions,
      topics: topicsOptions,
      branchOffices: branchOfficesOptions,
    }),
    [
      sourcesOptions,
      originsOptions,
      subOriginsOptions,
      brandsOptions,
      currentBrandsOptions,
      campaignsOptions,
      topicsOptions,
      branchOfficesOptions,
    ]
  );
}
